import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { applyLogEventRewards, updateGamificationModal } from './gamification_slice';
import { isForLens, lensParams, selectLensGroupId, type LensMeta } from './groups_slice';
import axiosInstance from '../../_helpers/axios_instance';
import {
  DEFAULT_SORT, defaultContentFilters, expandLicenseTags, maxLengthValue, type ContentFilters as ContentFilterValues,
} from '../../_helpers/content_filter_constants';
import { convertTimeToSeconds } from '../../_helpers/misc_functions';
import { logEvent } from '../../_helpers/UtilityReactComponents/UtilizationTracking';
import { buildWatchEntry, type SavedWatchEntry } from '../../_helpers/watch_progress';
import { isInProgressEntry } from '../../_helpers/ec_lessons';

export interface EcContent {
  id: number;
  created_at?: string;
  record_id_: string | number;
  lesson_number: string;
  title: string;
  tagline: string;
  description: string;
  content_type: string;
  content_status: string;
  length: string;
  subject_matter_expert: string;
  cme?: string | null;
  contact_hours?: string | null;
  cmefy_url?: string | null;
  ec_content_tags?: Array<{ id?: number; name: string }>;
  ec_category_id?: number | null;
  deeper_1?: string;
  deeper_2?: string;
  deeper_3?: string;
  wider_1?: string;
  wider_2?: string;
  wider_3?: string;
  order?: string | number | null;
  view_count?: number;
  cme_enabled?: boolean;
  content_url?: string | null;
  thumbnail_url?: string | null;
  // Stored by the API's subtitles service, but EcContentSerializer doesn't send it.
  subtitle_file_url?: string | null;
  objective_1?: string | null;
  objective_2?: string | null;
  objective_3?: string | null;
  recommended_reading_1?: string | null;
  recommended_reading_2?: string | null;
  recommended_reading_3?: string | null;
  web_url_recommended_reading_1?: string | null;
  web_url_recommended_reading_2?: string | null;
  web_url_recommended_reading_3?: string | null;
  s3_url_recommended_reading_1?: string | null;
  s3_url_recommended_reading_2?: string | null;
  s3_url_recommended_reading_3?: string | null;
  /** '|'-separated, matched by position with reference_urls. */
  references?: string | null;
  reference_urls?: string | null;
  /** Scored for the signed-in viewer by the API's RecommendationService. */
  recommendation_score?: { total_score: number; breakdown?: Record<string, number> } | null;
  [key: string]: unknown;
}

export interface EcSection {
  id: number;
  record_id_?: number;
  section_name: string;
  /** The API sends a boolean or 0/1; test it by truthiness. */
  show: boolean | number;
  ec_contents?: number[];
  order?: number | null;
  description_text?: string | null;
  profile_picture_url?: string | null;
  practice_group_ids?: number[];
  [key: string]: unknown;
}

export interface EcCategory {
  id: number;
  category_name: string;
  category_introduction?: string | null;
  number?: number | null;
  [key: string]: unknown;
}

/** A category with the items of one series in it. */
export type SeriesCategory = EcCategory & { content: EcContent[] };

export interface EcSpeaker {
  id: number;
  name: string;
  bio?: string | null;
  profile_picture_url?: string | null;
  ec_contents?: number[];
  [key: string]: unknown;
}

interface ContentFilters {
  [key: string]: unknown;
}

export interface EnrichmentCenterState {
  content: EcContent[];
  // Group the currently-loaded library was fetched for, so switching groups
  // re-fetches instead of showing the previous group's content.
  loadedForGroupId: number | null;
  fetchingEcContent: boolean;
  ecContentFetchError: string | null;
  contentFilters: ContentFilters;
  totalVideos: number;
  fetchingEcSections: boolean;
  ecSectionsFetchError?: string | null;
  sections: EcSection[];
  fetchingEcSpeakers: boolean;
  ecSpeakersFetchError: string | null;
  speakers: EcSpeaker[];
  fetchingEcCategories: boolean;
  ecCategoriesFetchError?: string | null;
  categories: EcCategory[];
  claimedContent: unknown;
  claimingCredit: boolean;
  claimCreditError: string | null;
  timeWatched: Record<string | number, unknown>;
  // The group lens (selectLensGroupId) timeWatched is for, and the one the
  // request in flight was made for.
  timeWatchedLensGroupId: number | null;
  requestLensGroupId: number | null;
  fetchingTimeWatched: boolean;
  timeWatchedError: string | null;
  sortValue: string;
  fetchingAchievementsProgress: boolean;
  achievementsProgressFetchError: string | null;
  achievementsProgress: Record<string, unknown>;
}

const initialState: EnrichmentCenterState = {
  content: [],
  loadedForGroupId: null,
  fetchingEcContent: false,
  ecContentFetchError: null,
  contentFilters: { ...defaultContentFilters },
  totalVideos: 0,
  fetchingEcSections: false,
  sections: [],
  fetchingEcSpeakers: false,
  ecSpeakersFetchError: null,
  speakers: [],
  fetchingEcCategories: false,
  categories: [],
  claimedContent: null,
  claimingCredit: false,
  claimCreditError: null,
  timeWatched: {},
  timeWatchedLensGroupId: null,
  requestLensGroupId: null,
  fetchingTimeWatched: false,
  timeWatchedError: null,
  sortValue: DEFAULT_SORT,
  fetchingAchievementsProgress: false,
  achievementsProgressFetchError: null,
  achievementsProgress: {},
};

/** Pass { force: true } to ask again even though the library has loaded, e.g.
 *  to retry a part of it (time watched, sections) that failed.
 *
 *  time_watched follows the group lens: a request for a new lens supersedes one
 *  in flight for the old lens, whose late answer is then dropped. */
export const getEcData = createAsyncThunk<
  any, { force?: boolean } | void, { state: RootState; pendingMeta: LensMeta; fulfilledMeta: LensMeta; rejectedMeta: LensMeta }
>(
  'enrichment_center/getEcData',
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    const meta = { lensGroupId: selectLensGroupId(getState()) };
    try {
      // Admins browse the app as a chosen group; the API narrows the library to
      // that group's content. Ignored by the API for non-admins.
      const viewingGroupId = getState().groups.currentGroup?.id ?? null;
      const groupParams = viewingGroupId ? { viewing_group_id: viewingGroupId } : {};
      const [content, sections, speakers, categories, timeWatched] = await Promise.allSettled([
        axiosInstance.get('/api/ec_contents', { params: { include: 'ec_content_tags', ...groupParams }, responseType: 'json' }),
        axiosInstance.get('/api/ec_sections', { params: { include: 'ec_contents', ...groupParams }, responseType: 'json' }),
        axiosInstance.get('/api/ec_speakers', { params: { include: 'ec_contents', ...groupParams }, responseType: 'json' }),
        axiosInstance.get('/api/ec_categories', { params: { include: 'ec_contents', ...groupParams }, responseType: 'json' }),
        axiosInstance.get('/api/enrichment_center/time_watched', meta.lensGroupId === null
          ? { responseType: 'json' }
          : { params: lensParams(meta.lensGroupId), responseType: 'json' }),
      ]);
      return fulfillWithValue({
        loadedForGroupId: viewingGroupId,
        content: content.status === 'fulfilled' ? content.value.data : null,
        contentError: content.status === 'rejected' ? (content.reason?.response?.data?.errors || content.reason?.message) : null,
        sections: sections.status === 'fulfilled' ? sections.value.data : null,
        sectionsError: sections.status === 'rejected' ? (sections.reason?.response?.data?.errors || sections.reason?.message) : null,
        speakers: speakers.status === 'fulfilled' ? speakers.value.data : null,
        speakersError: speakers.status === 'rejected' ? (speakers.reason?.response?.data?.errors || speakers.reason?.message) : null,
        categories: categories.status === 'fulfilled' ? categories.value.data : null,
        categoriesError: categories.status === 'rejected' ? (categories.reason?.response?.data?.errors || categories.reason?.message) : null,
        timeWatched: timeWatched.status === 'fulfilled' ? timeWatched.value.data : null,
        timeWatchedError: timeWatched.status === 'rejected' ? (timeWatched.reason?.response?.data?.errors || timeWatched.reason?.message) : null,
      }, meta);
    } catch (error: any) {
      return rejectWithValue(error.message, meta);
    }
  },
  {
    getPendingMeta: (_, { getState }) => ({ lensGroupId: selectLensGroupId(getState()) }),
    condition: (arg, { getState }) => {
      const { fetchingEcContent, content, loadedForGroupId, requestLensGroupId } = getState().enrichment_center;
      if (fetchingEcContent) return requestLensGroupId !== selectLensGroupId(getState());
      if (arg && arg.force) return true;
      // Re-fetch when the viewed group changed, even though content is loaded.
      const currentGroupId = getState().groups.currentGroup?.id ?? null;
      return content.length === 0 || loadedForGroupId !== currentGroupId;
    },
  }
);

export const getAchievementsProgress = createAsyncThunk<unknown, void, { state: RootState }>(
  'enrichment_center/getAchievementsProgress',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/achievements/check_progress', {
        params: { progress_type: 'category' },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => !getState().enrichment_center.fetchingAchievementsProgress,
  }
);

export const dispatchLogEventRewards = (logResult: any) => (dispatch: any) => {
  const achievements = logResult?.achievements_earned;
  const points = logResult?.points_earned;
  if (achievements?.length || points?.length) {
    dispatch(applyLogEventRewards({ achievements_earned: achievements, points_earned: points }));
  }
  if (achievements?.length) {
    achievements.forEach((a: any) => {
      dispatch(updateGamificationModal(true, {
        event: 'achievement',
        achievementName: a.achievement.name,
        iconUrl: a.achievement.icon_url,
      }));
    });
  }
  if (points?.length) {
    points.forEach((p: any) => {
      dispatch(updateGamificationModal(true, {
        event: 'points',
        pointsEarned: p.points ?? 0,
        pointsDescription: p.description,
      }));
    });
  }
};

export const logVideoEnded = createAsyncThunk<void, { record_id: string | number; totalTime: number; videoCollection?: string }>(
  'enrichment_center/logVideoEnded',
  async ({ record_id, totalTime, videoCollection }, { dispatch }) => {
    const logResult = await logEvent('video ended', record_id, totalTime, null, videoCollection);
    dispatch(dispatchLogEventRewards(logResult));
  }
);

export const logShare = createAsyncThunk<void, string | number>(
  'enrichment_center/logShare',
  async (record_id, { dispatch }) => {
    const logResult = await logEvent('share', record_id);
    dispatch(dispatchLogEventRewards(logResult));
  }
);

/**
 * Carries over progress made on the public share page, whose Login button sends
 * ?time_watched through the login redirect. It reads the live store, not a
 * render's snapshot: while time_watched is loading it does nothing, because the
 * fetch would overwrite it. The value comes from the URL, so it is capped at the
 * item's length, and it never moves a viewer back from a later saved position.
 */
export const importSharePageProgress = (record_id: number, seconds: number) =>
  (dispatch: (action: unknown) => unknown, getState: () => RootState) => {
    const enrichmentCenter = getState().enrichment_center;
    const item = getContentByRecordId(enrichmentCenter, record_id);
    if (!item || enrichmentCenter.fetchingTimeWatched || !Number.isFinite(seconds) || seconds <= 0) return;

    const lengthSeconds = convertTimeToSeconds(item.length);
    const importedSeconds = lengthSeconds > 0 ? Math.min(seconds, lengthSeconds) : seconds;
    const saved = enrichmentCenter.timeWatched[record_id] as SavedWatchEntry | undefined;
    if (importedSeconds <= (Number(saved?.seconds_watched) || 0)) return;

    dispatch(updateTimeWatched({ [record_id]: buildWatchEntry(record_id, importedSeconds, saved) }));
  };

export const recordContentView = createAsyncThunk<unknown, number | string>(
  'enrichment_center/recordContentView',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/ec_contents/${id}/view`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  }
);

const enrichmentCenterSlice = createSlice({
  name: 'enrichment_center',
  initialState,
  reducers: {
    updateTimeWatched(state, action: PayloadAction<Record<string | number, unknown>>) {
      Object.assign(state.timeWatched, action.payload);
    },
    setSortValue(state, action: PayloadAction<string>) {
      state.sortValue = action.payload;
    },
    setContentFilters(state, action: PayloadAction<Partial<ContentFilters>>) {
      Object.assign(state.contentFilters, action.payload);
    },
    clearContentFilters(state) {
      state.contentFilters = { ...defaultContentFilters };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEcData.pending, (state, action) => {
        state.requestLensGroupId = action.meta.lensGroupId ?? null;
        state.fetchingEcContent = true;
        state.fetchingEcSections = true;
        state.fetchingEcSpeakers = true;
        state.fetchingEcCategories = true;
        state.fetchingTimeWatched = true;
      })
      .addCase(getEcData.fulfilled, (state, action) => {
        if (!isForLens(action, state.requestLensGroupId)) return;
        const { content, contentError, sections, sectionsError, speakers, speakersError, categories, categoriesError, timeWatched, timeWatchedError, loadedForGroupId } = action.payload;
        state.loadedForGroupId = loadedForGroupId ?? null;
        state.fetchingEcContent = false;
        state.fetchingEcSections = false;
        state.fetchingEcSpeakers = false;
        state.fetchingEcCategories = false;
        state.fetchingTimeWatched = false;
        if (content !== null) {
          state.content = content;
          state.ecContentFetchError = null;
          state.totalVideos = content.filter((c: EcContent) =>
            (c.content_type === 'Video' || c.content_type === 'Audio') && c.content_status === 'Live on platform'
          ).length;
        } else {
          state.ecContentFetchError = contentError;
        }
        if (sections !== null) { state.sections = sections; state.ecSectionsFetchError = null; }
        else { state.ecSectionsFetchError = sectionsError; }
        if (speakers !== null) { state.speakers = speakers; state.ecSpeakersFetchError = null; }
        else { state.ecSpeakersFetchError = speakersError; }
        if (categories !== null) { state.categories = categories; state.ecCategoriesFetchError = null; }
        else { state.ecCategoriesFetchError = categoriesError; }
        if (timeWatched !== null) { state.timeWatched = timeWatched; state.timeWatchedError = null; }
        else { state.timeWatchedError = timeWatchedError; }
        state.timeWatchedLensGroupId = action.meta.lensGroupId ?? null;
      })
      .addCase(getEcData.rejected, (state, action) => {
        if (!isForLens(action, state.requestLensGroupId)) return;
        state.fetchingEcContent = false;
        state.fetchingEcSections = false;
        state.fetchingEcSpeakers = false;
        state.fetchingEcCategories = false;
        state.fetchingTimeWatched = false;
        state.ecContentFetchError = action.payload as string ?? null;
      });

    builder
      .addCase(getAchievementsProgress.pending, (state) => { state.fetchingAchievementsProgress = true; })
      .addCase(getAchievementsProgress.fulfilled, (state, action) => {
        state.fetchingAchievementsProgress = false;
        state.achievementsProgress = action.payload as Record<string, unknown>;
        state.achievementsProgressFetchError = null;
      })
      .addCase(getAchievementsProgress.rejected, (state, action) => {
        state.fetchingAchievementsProgress = false;
        state.achievementsProgressFetchError = action.payload as string ?? null;
      });
  },
});

export const {
  updateTimeWatched,
  setSortValue,
  setContentFilters,
  clearContentFilters,
} = enrichmentCenterSlice.actions;

export default enrichmentCenterSlice.reducer;

/**
 * Empties time_watched when it was loaded for a lens other than `lensGroupId`,
 * so nothing reads as watched until the new lens's answer arrives (getEcData
 * refetches on the group change that moved the lens). The root reducer runs
 * this after every action; with no lens it changes nothing.
 */
export const keepLens = (state: EnrichmentCenterState, lensGroupId: number | null): EnrichmentCenterState =>
  (state.timeWatchedLensGroupId ?? null) === lensGroupId
    ? state
    : { ...state, timeWatched: {}, timeWatchedLensGroupId: lensGroupId };

// --- Selectors (take the slice state directly, not RootState) ---

const fallbackContent: EcContent = {
  id: 0,
  record_id_: '',
  lesson_number: 'RL-100',
  title: '',
  tagline: '',
  description: '',
  content_type: 'Video',
  content_status: '',
  length: '1:23',
  subject_matter_expert: '',
  cme: '0',
  contact_hours: '0',
  ec_content_tags: [],
};

export const getFilteredContent = (enrichment_center: EnrichmentCenterState): EcContent[] => {
  const { content, contentFilters, timeWatched, categories } = enrichment_center;
  let filtered = content.filter((c) =>
    ((contentFilters['Media Type'] as string[]).length === 0 && (c.content_type === 'Video' || c.content_type === 'Audio')) ||
    (contentFilters['Media Type'] as string[]).includes(c.content_type)
  );
  filtered = filtered.filter((c) => {
    const combinedText = ''.concat(
      ...(c.ec_content_tags?.map((t) => t.name) ?? []),
      c.lesson_number, c.title, c.tagline,
      c.subject_matter_expert ?? '',
      (c.recommended_reading_1 as string) ?? '',
      (c.recommended_reading_2 as string) ?? '',
      (c.recommended_reading_3 as string) ?? '',
      (c.objective_1 as string) ?? '',
      (c.objective_2 as string) ?? '',
      (c.objective_3 as string) ?? '',
      c.description
    ).toLowerCase();
    return contentFilters['Text Search'] === '' || combinedText.includes((contentFilters['Text Search'] as string).toLowerCase());
  });
  const maxLength = contentFilters['Max Length'] as number;
  filtered = filtered.filter((c) => {
    // The slider's top means no limit, as the badges and active-filter count read it.
    return maxLength >= maxLengthValue || convertTimeToSeconds(c.length) <= maxLength;
  });
  filtered = filtered.filter((c) => !(contentFilters['CE Credit Eligible']) || c.cme_enabled);
  const licenseTags = expandLicenseTags(contentFilters['License Type'] as string[]);
  filtered = filtered.filter((c) =>
    licenseTags.length === 0 || c.ec_content_tags?.some((t) => licenseTags.includes(t.name))
  );
  filtered = filtered.filter((c) =>
    (contentFilters['Stressor'] as string[]).length === 0 || c.ec_content_tags?.some((t) => (contentFilters['Stressor'] as string[]).includes(t.name))
  );
  filtered = filtered.filter((c) => {
    const tc = timeWatched[c.record_id_] as any;
    return !(contentFilters['Hide Fully Watched Content']) || !tc || !tc?.video_finished;
  });
  filtered = filtered.filter((c) => !(contentFilters['In Progress']) || isInProgressEntry(timeWatched[c.record_id_]));
  filtered = filtered.filter((c) =>
    (contentFilters['Speaker'] as string[]).length === 0 || (contentFilters['Speaker'] as string[]).includes(c.subject_matter_expert)
  );
  filtered = filtered.filter((c) => {
    if ((contentFilters['Category'] as string[]).length === 0) return true;
    const category = categories.find((cat) => cat.id === c.ec_category_id);
    return category && (contentFilters['Category'] as string[]).includes(category.category_name);
  });
  filtered = filtered.filter((c) =>
    (contentFilters['Series'] as string[]).length === 0 ||
    (contentFilters['Series'] as string[]).includes(getSectionNameByRecordId(enrichment_center, c.record_id_))
  );
  return filtered;
};

/** getFilteredContent on exactly `filters` over the defaults, whatever the viewer has set in Browse, so a
 *  "See all N" count is what Browse shows once the link has set those filters. */
export const filteredContentFor = (ec: EnrichmentCenterState, filters: Partial<ContentFilterValues>): EcContent[] =>
  getFilteredContent({ ...ec, contentFilters: { ...defaultContentFilters, ...filters } });

export const getContentByRecordId = (enrichment_center: Pick<EnrichmentCenterState, 'content'>, record_id: string | number): EcContent | undefined => {
  return enrichment_center.content.find((c) => c.record_id_ === record_id || Number(c.record_id_) === Number(record_id));
};

export const getContentAndDeeperWider = (enrichment_center: Pick<EnrichmentCenterState, 'content'>, record_id: string | number) => {
  const { content } = enrichment_center;
  const contentLoaded = content.length > 0;
  const main = (contentLoaded
    ? content.find((c) => (c.record_id_ === record_id || Number(c.record_id_) === Number(record_id)) && c.content_type?.toLowerCase() !== 'handout')
    : null) ?? fallbackContent;
  const handout = contentLoaded && main
    ? content.find((c) => c.lesson_number === main.lesson_number && c.content_type?.toLowerCase() === 'handout')
    : fallbackContent;
  const deeper = contentLoaded && main
    ? content.filter((c) => [main.deeper_1, main.deeper_2, main.deeper_3].includes(c.lesson_number) && c.content_type?.toLowerCase() !== 'handout')
        .sort((a, b) => parseInt(a.order as string) - parseInt(b.order as string))
    : [];
  const wider = contentLoaded && main
    ? content.filter((c) => [main.wider_1, main.wider_2, main.wider_3].includes(c.lesson_number) && c.content_type?.toLowerCase() !== 'handout')
        .sort((a, b) => parseInt(a.order as string) - parseInt(b.order as string))
    : [];
  return { main, handout, deeper, wider };
};

/**
 * The series an item belongs to: its section and category, the section's items
 * (this one included, handouts left out), and those items grouped under the
 * categories they fall in, empty categories dropped.
 */
export const getSeriesForContent = (
  enrichment_center: Pick<EnrichmentCenterState, 'content' | 'sections' | 'categories'>,
  record_id: string | number,
) => {
  const { content, sections, categories } = enrichment_center;
  const item = getContentByRecordId(enrichment_center, record_id);
  const section = item ? sections.find((s) => s.ec_contents?.includes(item.id)) : undefined;
  const category = categories.find((c) => c.id === item?.ec_category_id);
  const seriesContent = (section?.ec_contents ?? [])
    .map((id) => content.find((c) => c.id === id))
    .filter((c): c is EcContent => !!c && c.content_type !== 'Handout');
  const seriesCategories: SeriesCategory[] = categories
    .map((c) => ({ ...c, content: seriesContent.filter((s) => s.ec_category_id === c.id) }))
    .filter((c) => c.content.length > 0);
  return { section, category, seriesContent, seriesCategories };
};

export const getSectionName = (enrichment_center: EnrichmentCenterState, section: number): string => {
  const result = enrichment_center.sections.find((s) => s.record_id_ === section);
  return result?.section_name ?? '';
};

export const getSectionByRecordId = (enrichment_center: EnrichmentCenterState, record_id_: string | number): number | undefined => {
  const item = enrichment_center.content.find((c) => c.record_id_ === record_id_ || Number(c.record_id_) === Number(record_id_));
  return enrichment_center.sections.find((s) => s.ec_contents?.includes(item?.id ?? -1))?.record_id_;
};

export const getSectionNameByRecordId = (enrichment_center: EnrichmentCenterState, record_id_: string | number): string => {
  const item = enrichment_center.content.find((c) => c.record_id_ === record_id_ || Number(c.record_id_) === Number(record_id_));
  return enrichment_center.sections.find((s) => s.ec_contents?.includes(item?.id ?? -1))?.section_name ?? '';
};

export const getSectionsWithContent = (enrichment_center: EnrichmentCenterState): string[] =>
  enrichment_center.sections.filter((s) => s.show && (s.ec_contents?.length ?? 0) > 0).map((s) => s.section_name);

export const getSpeakersWithContent = (enrichment_center: EnrichmentCenterState): string[] =>
  enrichment_center.speakers.filter((s) => (s.ec_contents?.length ?? 0) > 0).map((s) => s.name);

export const getStressorsWithContent = (enrichment_center: EnrichmentCenterState, stressors: string[]): string[] => {
  const { content } = enrichment_center;
  const result = new Set<string>();
  stressors.forEach((stressor) => {
    if (content.some((c) => c.ec_content_tags?.some((t) => t.name === stressor))) result.add(stressor);
  });
  return Array.from(result);
};

export const getNumberOfActiveFilters = (enrichment_center: EnrichmentCenterState): number =>
  Object.values(enrichment_center.contentFilters)
    .map((v) => {
      if (Array.isArray(v)) return v.length;
      if (typeof v === 'string') return v === '' ? 0 : 1;
      if (typeof v === 'boolean') return v ? 1 : 0;
      if (typeof v === 'number') return v === maxLengthValue ? 0 : 1;
      return 0;
    })
    .reduce((acc, n) => acc + n, 0);

export const getContentWithTimeWatched = (enrichment_center: EnrichmentCenterState): EcContent[] => {
  const {content, timeWatched} = enrichment_center
  return content.map((c) => {
    return {...c, time_watched: timeWatched[c.record_id_]}
  })
}